import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { useFAQs } from '../hooks/useFAQs'
import SectionHeading from './ui/SectionHeading'
import type { FAQ as FAQType } from '../types/wp'

// Fallback FAQs shown when WordPress is not connected
const fallbackFAQs: FAQType[] = [
  {
    id: 1,
    question: {
      hu: 'Milyen korú gyerekek vehetnek részt a táborban?',
      ro: 'Ce vârstă trebuie să aibă copiii pentru a participa la tabără?',
    },
    answer: {
      hu: 'Két korosztály számára szervezünk tábort: 1–4. osztályosoknak (6–10 év) és 5–8. osztályosoknak (10–14 év). A programokat az adott korosztály igényeihez igazítjuk.',
      ro: 'Organizăm tabere pentru două grupe de vârstă: clasele 1–4 (6–10 ani) și clasele 5–8 (10–14 ani). Programele sunt adaptate nevoilor fiecărei grupe.',
    },
  },
  {
    id: 2,
    question: {
      hu: 'Mi van, ha a gyermekem honvágyat érez?',
      ro: 'Ce se întâmplă dacă copilul meu simte dor de casă?',
    },
    answer: {
      hu: 'Tapasztalt csapatunk felkészült erre! A változatos programok, a barátságos légkör és az állandó felügyelet segít a gyerekeknek gyorsan beilleszkedni. Szükség esetén természetesen értesítjük a szülőket.',
      ro: 'Echipa noastră experimentată este pregătită pentru asta! Programele variate, atmosfera prietenoasă și supravegherea constantă ajută copiii să se integreze rapid. La nevoie, desigur, contactăm părinții.',
    },
  },
  {
    id: 3,
    question: {
      hu: 'Mit tartalmaz a tábor ára?',
      ro: 'Ce include prețul taberei?',
    },
    answer: {
      hu: 'Az ár tartalmazza a szállást, napi 4 étkezést (reggeli, ebéd, uzsonna, vacsora), az összes programot és tevékenységet, a felszerelések használatát és a folyamatos felügyeletet.',
      ro: 'Prețul include cazarea, 4 mese pe zi (mic dejun, prânz, gustare, cină), toate programele și activitățile, utilizarea echipamentelor și supravegherea continuă.',
    },
  },
  {
    id: 4,
    question: {
      hu: 'Hogyan biztosítják a gyerekek biztonságát?',
      ro: 'Cum este asigurată siguranța copiilor?',
    },
    answer: {
      hu: 'A helyszín egy 5 hektáros, lezárt területen helyezkedik el, forgalomtól távol. Képzett felügyelők biztosítják a folyamatos kontrollt. Minden kalandprogramot (mászófal, túrák) szakképzett instruktorok vezetnek.',
      ro: 'Locația se află pe un teren închis de 5 hectare, departe de trafic. Supraveghetori instruiți asigură controlul permanent. Toate programele de aventură (escaladă, excursii) sunt conduse de instructori calificați.',
    },
  },
  {
    id: 5,
    question: {
      hu: 'Lehet-e speciális étrendet kérni (allergia, vegetáriánus)?',
      ro: 'Se pot solicita meniuri speciale (alergii, vegetarian)?',
    },
    answer: {
      hu: 'Igen! Kérjük, a bejelentkezéskor jelezzék az étkezési igényeket vagy allergiákat. Séfünk alkalmazkodik minden különleges kéréshez.',
      ro: 'Da! Vă rugăm să menționați cerințele alimentare sau alergiile la înscriere. Bucătarul nostru se adaptează oricărei solicitări speciale.',
    },
  },
  {
    id: 6,
    question: {
      hu: 'Hogyan tudok jelentkezni?',
      ro: 'Cum mă pot înscrie?',
    },
    answer: {
      hu: 'A Kapcsolat oldalon kitöltheti az érdeklődési űrlapot, vagy közvetlenül felhívhat minket. A helyek száma korlátozott (44 férőhely), ezért érdemes mielőbb jelentkezni!',
      ro: 'Puteți completa formularul de interes pe pagina de Contact, sau ne puteți suna direct. Locurile sunt limitate (44 locuri), așa că vă recomandăm să vă înscrieti cât mai curând!',
    },
  },
]

export default function FAQ() {
  const { locale, t } = useLocale()
  const { faqs: apiFaqs, loading } = useFAQs()
  const [openId, setOpenId] = useState<number | null>(null)

  const faqs = apiFaqs.length > 0 ? apiFaqs : fallbackFAQs

  if (loading) return null

  return (
    <section className="section-padding bg-white">
      <div className="container-max">
        <SectionHeading title={t.faq.title} subtitle={t.faq.subtitle} />

        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((item) => {
            const isOpen = openId === item.id
            return (
              <div
                key={item.id}
                className="border border-border rounded-card overflow-hidden transition-shadow hover:shadow-subtle"
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${item.id}`}
                >
                  <span className="font-medium text-secondary text-sm md:text-base pr-4">
                    {item.question[locale]}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  id={`faq-answer-${item.id}`}
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-4 text-text-secondary text-sm leading-relaxed">
                    {item.answer[locale]}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
